import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '@/services/api'
import { toast } from 'sonner'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { dealsPagePathForDeal } from '@/utils/dealsPageNavigation'
import { isRocketInstance } from '@/config/instanceProfile'
import { requestLeadConvertOptions } from '@/stores/convertLeadDialogStore'
import type { CloserLead } from '@/types'
import {
  DEFAULT_MEETING_TYPE,
  isDirectProposalLeadConvert,
  leadConvertSuccessMessage,
  resolveLeadConvertEntryStage,
} from '@/components/Deals/DealDetailPanel/meetingTypeOptions'
import {
  clearPendingProposalSession,
  savePendingProposalSession,
} from '@/components/CloserLeads/closerLeadProposalFlow.util'
import { resolveConvertRequestDecision } from '@/components/CloserLeads/closerLeadConvertRequest.util'

export type CloserLeadConvertTarget = Pick<CloserLead, 'id' | 'empresa' | 'contato' | 'dealId' | 'parceiroId'>

type RequestConvertOptions = {
  onAfterSuccess?: () => void
  /** Abre modal mesmo com deal existente (nova negociação em Meus Clientes). */
  forceNew?: boolean
}

export function useCloserLeadConvertModal(onSuccess: () => void) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState<CloserLeadConvertTarget | null>(null)
  const [dataHora, setDataHora] = useState('')
  const [tipo, setTipo] = useState(DEFAULT_MEETING_TYPE)
  const [submitting, setSubmitting] = useState(false)
  const [afterSuccess, setAfterSuccess] = useState<(() => void) | null>(null)
  const [elaborateConfirmOpen, setElaborateConfirmOpen] = useState(false)
  const [proposalEditorDealId, setProposalEditorDealId] = useState<string | null>(null)

  const close = useCallback(() => {
    setOpen(false)
    setTarget(null)
    setDataHora('')
    setTipo(DEFAULT_MEETING_TYPE)
    setAfterSuccess(null)
    setElaborateConfirmOpen(false)
  }, [])

  const closeProposalEditor = useCallback(() => {
    setProposalEditorDealId(null)
  }, [])

  const openProposalEditor = useCallback((dealId: string, meta?: { leadId?: string; empresa?: string }) => {
    setProposalEditorDealId(dealId)
    savePendingProposalSession({
      dealId,
      leadId: meta?.leadId,
      empresa: meta?.empresa,
    })
  }, [])

  const dismissPendingProposal = useCallback(() => {
    clearPendingProposalSession()
    setProposalEditorDealId(null)
  }, [])

  const resumeProposalForLead = useCallback(
    (lead: CloserLeadConvertTarget & { dealId?: string; deals?: { id: string }[] }) => {
      const dealId = lead.dealId || lead.deals?.[0]?.id
      if (!dealId) {
        toast.error('Negociação não encontrada para retomar a proposta.')
        return
      }
      openProposalEditor(dealId, { leadId: lead.id, empresa: lead.empresa })
    },
    [openProposalEditor],
  )

  const requestConvert = useCallback(
    (lead: CloserLead | CloserLeadConvertTarget, options?: RequestConvertOptions) => {
      const decision = resolveConvertRequestDecision(lead, { forceNew: options?.forceNew })
      if (decision.action === 'resume_proposal') {
        resumeProposalForLead(lead)
        return
      }
      if (decision.action === 'navigate_deal') {
        navigate(dealsPagePathForDeal(decision.dealId))
        return
      }
      if (decision.action === 'already_converted') {
        toast.info('Este lead já foi convertido em uma Negociação')
        return
      }
      setTarget(lead)
      setDataHora('')
      setTipo(DEFAULT_MEETING_TYPE)
      setAfterSuccess(options?.onAfterSuccess ?? null)
      setOpen(true)
    },
    [resumeProposalForLead, navigate],
  )

  const requestNewNegotiation = useCallback(
    (lead: CloserLead | CloserLeadConvertTarget, options?: Omit<RequestConvertOptions, 'forceNew'>) => {
      requestConvert(lead, { ...options, forceNew: true })
    },
    [requestConvert],
  )

  const executeConvert = useCallback(
    async (shouldOpenProposalEditor: boolean) => {
      if (!target) return
      if (!dataHora.trim()) {
        toast.error('Selecione a data e a hora da reunião para abrir a negociação.')
        return
      }

      const meetingDate = new Date(dataHora)
      if (Number.isNaN(meetingDate.getTime())) {
        toast.error('Data da reunião inválida.')
        return
      }

      const etapa = resolveLeadConvertEntryStage(tipo)
      const directProposal = isDirectProposalLeadConvert(tipo)

      setSubmitting(true)
      try {
        let partnerOptions: Record<string, unknown> = {}
        if (isRocketInstance) {
          const options = await requestLeadConvertOptions({
            leadId: target.id,
            empresa: target.empresa,
            contato: target.contato,
            parceiroId: target.parceiroId,
          })
          if (!options) return
          partnerOptions = options
        }

        const { data } = await api.post(`/closer-leads/${target.id}/converter`, {
          etapa,
          valor: 0,
          dataReuniao: meetingDate.toISOString(),
          tipoReuniao: tipo,
          ...partnerOptions,
        })
        toast.success(leadConvertSuccessMessage(tipo))
        const dealId = data?.data?.deal?.id as string | undefined
        const callback = afterSuccess
        close()
        callback?.()
        onSuccess()

        if (directProposal && shouldOpenProposalEditor && dealId) {
          openProposalEditor(dealId, { leadId: target.id, empresa: target.empresa })
          return
        }

        if (directProposal && dealId) {
          savePendingProposalSession({
            dealId,
            leadId: target.id,
            empresa: target.empresa,
          })
          navigate(dealsPagePathForDeal(dealId))
        }
      } catch (error: unknown) {
        toast.error(getFriendlyError(error, 'Erro ao converter lead'))
      } finally {
        setSubmitting(false)
        setElaborateConfirmOpen(false)
      }
    },
    [target, dataHora, tipo, afterSuccess, close, onSuccess, navigate, openProposalEditor],
  )

  const handleSubmitClick = useCallback(() => {
    if (isDirectProposalLeadConvert(tipo)) {
      setElaborateConfirmOpen(true)
      return
    }
    void executeConvert(false)
  }, [tipo, executeConvert])

  const confirmElaborateNow = useCallback(() => {
    void executeConvert(true)
  }, [executeConvert])

  const confirmElaborateLater = useCallback(() => {
    void executeConvert(false)
  }, [executeConvert])

  return {
    open,
    target,
    dataHora,
    setDataHora,
    tipo,
    setTipo,
    submitting,
    requestConvert,
    requestNewNegotiation,
    close,
    handleSubmitClick,
    elaborateConfirmOpen,
    setElaborateConfirmOpen,
    confirmElaborateNow,
    confirmElaborateLater,
    proposalEditorDealId,
    closeProposalEditor,
    openProposalEditor,
    resumeProposalForLead,
    dismissPendingProposal,
  }
}
